import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import Reveal from "../components/Reveal";
import { Phone } from "lucide-react";
import { trackPhoneClick } from "../lib/analytics";

const FAQS = [
    {
        q: "What is an Employer of Record (EOR)?",
        a: "An Employer of Record is an arrangement in which a third party takes on designated employment administration responsibilities for workers engaged by another business. It allows companies to explore employing people in markets where they do not have their own local entity, while the day-to-day work remains directed by the client business.",
    },
    {
        q: "Which markets does Hexagon EOR support?",
        a: "Hexagon EOR focuses primarily on supporting United States businesses, with additional service availability in Canada. Market coverage varies by engagement, and our team confirms availability for your specific situation during an initial call.",
    },
    {
        q: "How do we get started with Hexagon EOR?",
        a: "The first step is a phone conversation with our team. Call +1 833-343-9367 to discuss your hiring goals, review suitable employment arrangements, and decide whether to proceed. There is no obligation attached to an initial call.",
    },
    {
        q: "Does Hexagon EOR provide legal, tax, or immigration advice?",
        a: "Alongside employer of record and workforce administration, Hexagon provides legal and tax services related to international employment, and our team can explain the scope available for your situation on a call. We do not provide immigration services; for immigration matters we recommend consulting a qualified immigration professional.",
    },
    {
        q: "Is an EOR the same as a staffing agency?",
        a: "No. A staffing agency recruits and supplies workers. An employer of record arrangement focuses on employment administration for people your business has already chosen to engage — it is not a recruitment or talent-sourcing service.",
    },
    {
        q: "Will Hexagon replace our internal HR team?",
        a: "No. EOR and workforce administration services complement your internal team by taking on defined administrative responsibilities. Your business continues to direct the day-to-day work of your people.",
    },
    {
        q: "How much do EOR services cost?",
        a: "Pricing depends on the scope of services, the markets involved, and the size of your engagement. We discuss pricing openly during a consultation call, once we understand your situation — there are no obligations attached to that conversation.",
    },
    {
        q: "How quickly can we hire internationally?",
        a: "Timelines vary by market, role, and the arrangement involved, and no universal timeline applies. On an initial call, our team will give you realistic expectations for your specific situation rather than a generic promise.",
    },
];

export default function Faq() {
    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="bg-white py-24 lg:py-32"
        >
            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                            <span className="h-px w-10 bg-gold" aria-hidden="true" />
                            Common Questions
                        </p>
                        <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                            Frequently asked questions
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-navy/65">
                            Straight answers about EOR arrangements, market
                            coverage, and how we work. For anything specific to
                            your business, a short call is the fastest route.
                        </p>
                        <a
                            href="tel:+18333439367"
                            data-testid="faq-call-cta"
                            onClick={() => trackPhoneClick("faq")}
                            className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-navy/20 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-navy transition-[background-color,border-color,color] duration-200 hover:border-navy hover:bg-navy hover:text-white"
                        >
                            <Phone size={15} aria-hidden="true" />
                            Ask Us Directly
                        </a>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="lg:col-span-7">
                    <Accordion
                        type="single"
                        collapsible
                        data-testid="faq-accordion"
                        className="space-y-3"
                    >
                        {FAQS.map((item, i) => (
                            <AccordionItem
                                key={item.q}
                                value={`item-${i}`}
                                className="rounded-xl border border-navy/10 bg-mist px-6 data-[state=open]:border-gold/60"
                            >
                                <AccordionTrigger
                                    data-testid={`faq-question-${i}`}
                                    className="py-5 text-left font-display text-base font-bold tracking-tight text-navy hover:no-underline"
                                >
                                    {item.q}
                                </AccordionTrigger>
                                <AccordionContent
                                    data-testid={`faq-answer-${i}`}
                                    className="text-sm leading-relaxed text-navy/65"
                                >
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </div>
        </section>
    );
}
